import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Camera, Check, ChevronLeft, ChevronRight, FileText, FolderOpen, UserRound } from "lucide-react";
import { EASE_EXPO, SPRING_POP } from "@/components/motion/primitives";
import { FEES } from "@/data/fees";
import { C } from "./parts";

/*
 * The Migrada app, in use, inside the phone on the desk. A finger swipes the
 * "Ready now" facts, scrolls to the route, opens the Money stage, uploads a
 * bank statement, watches it get checked and goes back home one gate
 * further. Copy, stages and checks mirror the real app (src/case): change
 * them together. The app area is 282 × 451 px; finger positions are in those
 * units, measured from the rendered screens.
 */

const eur = (n: number) => `€${n.toLocaleString("en-GB")}`;
const FUNDS = eur(FEES.FINANCIAL_UNDER_24_ANNUAL);

/** Each phase and how long it lasts, in ms. */
const PHASES = [
  ["home", 1200],
  ["touch-tiles", 350],
  ["swipe-tiles", 1300],
  ["touch-page", 350],
  ["scroll", 1500],
  ["press-money", 450],
  ["stage", 1200],
  ["press-upload", 400],
  ["sheet", 1000],
  ["press-file", 400],
  ["uploading", 1300],
  ["checking", 1500],
  ["checked", 1700],
  ["press-back", 450],
  ["home-after", 2800],
] as const;
type Phase = (typeof PHASES)[number][0];
const ORDER = PHASES.map(([p]) => p);
const at = (p: Phase, from: Phase) => ORDER.indexOf(p) >= ORDER.indexOf(from);

/** Where the finger is in each phase (null = lifted). */
const FINGER: Partial<Record<Phase, { x: number; y: number; press?: boolean }>> = {
  "touch-tiles": { x: 214, y: 238 },
  "swipe-tiles": { x: 74, y: 238, press: true },
  "touch-page": { x: 150, y: 410 },
  scroll: { x: 150, y: 190, press: true },
  "press-money": { x: 143, y: 272, press: true },
  "press-upload": { x: 218, y: 218, press: true },
  "press-file": { x: 151, y: 409, press: true },
  "press-back": { x: 23, y: 33, press: true },
};

const ROUTE = [
  ["Your plan", "done"],
  ["University admission", "done"],
  ["Documents", "6 of 8"],
  ["Money", "now"],
  ["Insurance and housing", ""],
  ["Embassy", ""],
  ["Arrival", ""],
] as const;

function Tick() {
  return (
    <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: C.TEAL_DEEP }}>
      <Check className="h-3 w-3 text-white" strokeWidth={3.4} />
    </span>
  );
}

function Dots() {
  return (
    <span className="inline-flex gap-1">
      {[0, 1, 2].map((i) => (
        <span key={i} className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: C.TEAL_DEEP, animation: `case-dot 1.2s ${i * 0.15}s infinite ease-in-out` }} />
      ))}
    </span>
  );
}

function Home({ phase }: { phase: Phase }) {
  const after = at(phase, "home-after");
  const tilesX = at(phase, "swipe-tiles") && !after ? -150 : 0;
  const scrollY = at(phase, "scroll") && !after ? -200 : 0;
  const pressed = phase === "press-money";
  const tiles = [
    ["Money to show", FUNDS, "for one year"],
    ["Permit fee", eur(FEES.APPLICATION), "card included"],
    ["Apply by", "3 Jul", "2027"],
    ["Embassy", "12 days", "to go"],
  ];
  return (
    <motion.div className="px-4" animate={{ y: scrollY }} transition={{ duration: 1.1, ease: EASE_EXPO }}>
      <div className="flex items-center justify-between pt-1">
        <span className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-full" style={{ backgroundColor: C.OAK_SOFT, color: C.OAK }}>
            <UserRound className="h-[18px] w-[18px]" strokeWidth={2.2} />
          </span>
          <span className="leading-tight">
            <span className="block text-[11.5px] font-semibold" style={{ color: C.MUTED }}>
              Hi there
            </span>
            <span className="block text-[17px] font-extrabold tracking-[-0.02em]" style={{ color: C.INK }}>
              Your case
            </span>
          </span>
        </span>
        <motion.span key={after ? "g5" : "g4"} className="rounded-full px-2.5 py-1 text-[11px] font-bold" style={{ backgroundColor: after ? C.TEAL : C.PANEL, color: after ? C.BRAND_DARK : C.SECONDARY }} initial={after ? { scale: 0.6 } : false} animate={{ scale: 1 }} transition={SPRING_POP}>
          Gate {after ? 5 : 4} of 7
        </motion.span>
      </div>

      {/* Next step */}
      <div className="mt-3 rounded-[18px] p-3.5" style={{ backgroundColor: C.TEAL_SOFT }}>
        <span className="text-[10.5px] font-bold uppercase tracking-[0.08em]" style={{ color: C.TEAL_DEEP }}>
          Next step
        </span>
        <AnimatePresence mode="wait" initial={false}>
          <motion.p key={after ? "ins" : "bank"} className="mt-0.5 text-[15px] font-extrabold leading-snug tracking-[-0.01em]" style={{ color: C.INK }} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.35 }}>
            {after ? "Get health insurance" : "Upload your bank statement"}
          </motion.p>
        </AnimatePresence>
        <span className="mt-2 inline-flex rounded-full px-3 py-1 text-[11.5px] font-bold" style={{ backgroundColor: C.BRAND_DARK, color: "#FFFFFF" }}>
          {after ? "See options" : "Upload"}
        </span>
      </div>

      {/* Ready now: a row of facts that swipes. */}
      <p className="mt-4 text-[13px] font-extrabold" style={{ color: C.INK }}>
        Ready now
      </p>
      <div className="-mx-4 mt-2 overflow-hidden px-4">
        <motion.div className="flex gap-2" animate={{ x: tilesX }} transition={{ duration: 1.1, ease: EASE_EXPO }}>
          {tiles.map(([k, v, s]) => (
            <div key={k} className="flex h-[86px] w-[120px] shrink-0 flex-col justify-between rounded-[16px] p-2.5" style={{ backgroundColor: C.PANEL }}>
              <span className="text-[10.5px] font-semibold" style={{ color: C.MUTED }}>
                {k}
              </span>
              <span>
                <span className="block text-[19px] font-black leading-none tracking-[-0.03em]" style={{ color: C.INK }}>
                  {v}
                </span>
                <span className="text-[10px] font-semibold" style={{ color: C.MUTED }}>
                  {s}
                </span>
              </span>
            </div>
          ))}
        </motion.div>
      </div>

      {/* The route. */}
      <p className="mt-4 text-[13px] font-extrabold" style={{ color: C.INK }}>
        Your route
      </p>
      <ul className="mt-1.5">
        {ROUTE.map(([name, state], i) => {
          const money = name === "Money";
          const done = state === "done" || (after && money);
          const now = (money && !after) || (after && name === "Insurance and housing");
          return (
            <motion.li
              key={name}
              className="flex h-[44px] items-center gap-2.5 rounded-[12px] px-2"
              animate={{ backgroundColor: money && pressed ? C.TEAL_SOFT : now ? C.PANEL : "rgba(0,0,0,0)", scale: money && pressed ? 0.98 : 1 }}
              transition={{ duration: 0.2 }}
            >
              {done ? (
                <Tick />
              ) : (
                <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-2 text-[10px] font-black" style={{ borderColor: now ? C.TEAL_DEEP : C.RULE, color: now ? C.TEAL_DEEP : C.MUTED }}>
                  {i + 1}
                </span>
              )}
              <span className="flex-1 text-[13px] font-bold" style={{ color: done ? C.MUTED : C.INK }}>
                {name}
              </span>
              {state && state !== "done" && state !== "now" && <span className="text-[11px] font-semibold" style={{ color: C.MUTED }}>{state}</span>}
              {now && <ChevronRight className="h-4 w-4" style={{ color: C.TEAL_DEEP }} />}
            </motion.li>
          );
        })}
      </ul>
    </motion.div>
  );
}

function MoneyStage({ phase }: { phase: Phase }) {
  const uploading = phase === "uploading";
  const checking = phase === "checking";
  const checked = at(phase, "checked");
  const hasFile = at(phase, "uploading");
  return (
    <div className="relative h-full px-4">
      <div className="flex h-[44px] items-center gap-1">
        <motion.span animate={{ scale: phase === "press-back" ? 0.85 : 1 }} className="-ml-1">
          <ChevronLeft className="h-6 w-6" style={{ color: C.INK }} />
        </motion.span>
        <span className="flex-1 text-[18px] font-extrabold tracking-[-0.02em]" style={{ color: C.INK }}>
          Money
        </span>
        <span className="text-[11px] font-bold" style={{ color: C.MUTED }}>
          Gate 4 of 7
        </span>
      </div>
      <p className="text-[12px] leading-snug" style={{ color: C.SECONDARY }}>
        Austria asks to see that you can cover a year. It is your own money, not a fee.
      </p>

      <ul className="mt-3 space-y-2">
        <li className="flex items-center gap-2.5 rounded-[14px] p-2.5" style={{ backgroundColor: C.PANEL }}>
          <Tick />
          <span className="text-[12.5px] font-bold" style={{ color: C.MUTED }}>
            Have {FUNDS} ready for one year
          </span>
        </li>
        <li className="flex items-center gap-2.5 rounded-[14px] p-2.5" style={{ backgroundColor: C.PANEL }}>
          <Tick />
          <span className="text-[12.5px] font-bold" style={{ color: C.MUTED }}>
            Keep it in an account in your name
          </span>
        </li>
        <motion.li layout className="rounded-[14px] border p-2.5" style={{ borderColor: checked ? C.TEAL_DEEP : C.RULE }}>
          <div className="flex items-center gap-2.5">
            {checked ? (
              <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={SPRING_POP}>
                <Tick />
              </motion.span>
            ) : (
              <span className="h-[22px] w-[22px] shrink-0 rounded-full border-2" style={{ borderColor: C.TEAL_DEEP }} />
            )}
            <span className="flex-1 text-[12.5px] font-bold" style={{ color: C.INK }}>
              Upload your bank statement
            </span>
            {!hasFile && (
              <motion.span className="rounded-full px-3 py-1 text-[11px] font-bold" style={{ backgroundColor: C.BRAND_DARK, color: "#FFFFFF" }} animate={{ scale: phase === "press-upload" ? 0.9 : 1 }}>
                Upload
              </motion.span>
            )}
          </div>

          {hasFile && (
            <motion.div className="mt-2.5 rounded-[12px] p-2" style={{ backgroundColor: C.PANEL }} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex items-center gap-2">
                <span className="relative flex h-8 w-8 items-center justify-center overflow-hidden rounded-[8px] bg-white">
                  <FileText className="h-4 w-4" style={{ color: C.TEAL_DEEP }} />
                  {checking && <span className="absolute inset-x-0 top-0 h-1/3" style={{ background: "linear-gradient(180deg, rgba(46,230,214,0) 0%, rgba(46,230,214,0.6) 100%)", animation: "case-scan 1s ease-in-out infinite" }} />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[11.5px] font-bold" style={{ color: C.INK }}>
                    bank-statement.pdf
                  </span>
                  {uploading ? (
                    <span className="mt-1 block h-1 overflow-hidden rounded-full bg-white">
                      <motion.span className="block h-full rounded-full" style={{ backgroundColor: C.TEAL }} initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ duration: 1.15, ease: "easeInOut" }} />
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-[10.5px] font-bold" style={{ color: checked ? C.TEAL_DEEP : C.MUTED }}>
                      {checked ? "Looks good" : <>Checking <Dots /></>}
                    </span>
                  )}
                </span>
              </div>
              {checked && (
                <ul className="mt-2 space-y-1">
                  {["Your name matches", `€9,200 covers ${FUNDS}`, "The last 3 months"].map((t, i) => (
                    <motion.li key={t} className="flex items-center gap-1.5 text-[10.5px] font-semibold" style={{ color: C.SECONDARY }} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + i * 0.18 }}>
                      <Check className="h-3 w-3" strokeWidth={3} style={{ color: C.TEAL_DEEP }} />
                      {t}
                    </motion.li>
                  ))}
                </ul>
              )}
            </motion.div>
          )}
        </motion.li>
      </ul>

      {/* The upload sheet. */}
      <AnimatePresence>
        {(phase === "sheet" || phase === "press-file") && (
          <>
            <motion.div className="absolute inset-0 -top-[52px] bg-black/25" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />
            <motion.div className="absolute inset-x-0 bottom-0 rounded-t-[22px] bg-white px-4 pb-4 pt-2" initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ duration: 0.4, ease: EASE_EXPO }}>
              <span className="mx-auto block h-1 w-9 rounded-full" style={{ backgroundColor: C.RULE }} />
              <p className="mt-3 text-[14px] font-extrabold" style={{ color: C.INK }}>
                Add your bank statement
              </p>
              {[
                [Camera, "Take a photo"],
                [FolderOpen, "Choose a file"],
              ].map(([Icon, label]) => {
                const I = Icon as typeof Camera;
                const active = label === "Choose a file" && phase === "press-file";
                return (
                  <motion.div key={label as string} className="mt-2 flex items-center gap-3 rounded-[14px] px-3 py-3" animate={{ backgroundColor: active ? C.TEAL_SOFT : C.PANEL, scale: active ? 0.98 : 1 }}>
                    <I className="h-5 w-5" style={{ color: C.BRAND_DARK }} />
                    <span className="text-[13px] font-bold" style={{ color: C.INK }}>
                      {label as string}
                    </span>
                  </motion.div>
                );
              })}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

/** The app playing; `running` is false when off screen or paused. */
export function AppDemo({ running }: { running: boolean }) {
  const [i, setI] = useState(0);
  const [cycle, setCycle] = useState(0);
  const phase = PHASES[i][0];

  useEffect(() => {
    if (!running) return;
    const t = window.setTimeout(() => {
      if (i === PHASES.length - 1) {
        setI(0);
        setCycle((c) => c + 1);
      } else setI(i + 1);
    }, PHASES[i][1]);
    return () => window.clearTimeout(t);
  }, [i, running]);

  const inStage = at(phase, "stage") && !at(phase, "home-after");
  const finger = FINGER[phase];

  return (
    <div className="relative h-full overflow-hidden">
      <AnimatePresence initial={false} mode="popLayout">
        {inStage ? (
          <motion.div key={`stage-${cycle}`} className="absolute inset-0 bg-white" initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ duration: 0.45, ease: EASE_EXPO }}>
            <MoneyStage phase={phase} />
          </motion.div>
        ) : (
          <motion.div key={`home-${cycle}`} className="absolute inset-0 bg-white" initial={{ x: "-25%" }} animate={{ x: 0 }} exit={{ x: "-25%" }} transition={{ duration: 0.45, ease: EASE_EXPO }}>
            <Home phase={phase} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* The toast after coming back. */}
      <AnimatePresence>
        {phase === "home-after" && (
          <motion.div className="absolute inset-x-3 top-1 z-20 flex items-center gap-2 rounded-[14px] px-3 py-2.5 shadow-[0_10px_24px_rgba(10,15,14,0.18)]" style={{ backgroundColor: C.BRAND_DARK }} initial={{ y: -60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -60, opacity: 0 }} transition={{ delay: 0.5, type: "spring", stiffness: 260, damping: 24 }}>
            <span className="flex h-5 w-5 items-center justify-center rounded-full" style={{ backgroundColor: C.TEAL }}>
              <Check className="h-3 w-3" strokeWidth={3.4} style={{ color: C.BRAND_DARK }} />
            </span>
            <span className="text-[12px] font-bold text-white">Bank statement looks good</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* The finger. */}
      <motion.span
        className="pointer-events-none absolute left-0 top-0 z-30 h-9 w-9 rounded-full"
        style={{ backgroundColor: "rgba(10,15,14,0.18)", boxShadow: "0 0 0 2px rgba(255,255,255,0.85), 0 4px 10px rgba(10,15,14,0.2)" }}
        initial={false}
        animate={finger ? { x: finger.x - 18, y: finger.y - 18, opacity: 1, scale: finger.press ? 0.82 : 1 } : { opacity: 0, scale: 1.3 }}
        transition={{ x: { duration: phase === "swipe-tiles" || phase === "scroll" ? 1.0 : 0.45, ease: EASE_EXPO }, y: { duration: phase === "swipe-tiles" || phase === "scroll" ? 1.0 : 0.45, ease: EASE_EXPO }, opacity: { duration: 0.2 }, scale: { duration: 0.15 } }}
      />
    </div>
  );
}
